/**
 * Draggable pill for a location-tied task that could be opportunistically
 * completed "when you're near X". Lives in the OpportunisticLane strip on
 * week/day views. Drag to schedule on a time slot — the pill stays in the
 * lane because the user is committing to a time *and* keeping the geofence
 * armed (location is still the home base for the task).
 */

import React from 'react'
import { useDraggable } from '@dnd-kit/core'
import { CSS } from '@dnd-kit/utilities'
import { MapPin } from 'lucide-react'
import type { Task } from '../../services/taskService'

export const LOCATION_PILL_DRAG_TYPE = 'location_pill'

export interface LocationPillDragData {
  kind: typeof LOCATION_PILL_DRAG_TYPE
  taskId: number
  task: Task
  placeTitle: string | null
}

interface LocationPillProps {
  task: Task
  placeTitle: string | null
  /** nav to task detail on plain click (not drag) */
  onOpen?: (taskId: number) => void
}

export const LocationPill: React.FC<LocationPillProps> = ({
  task,
  placeTitle,
  onOpen,
}) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `${LOCATION_PILL_DRAG_TYPE}:${task.id}`,
    data: {
      kind: LOCATION_PILL_DRAG_TYPE,
      taskId: task.id,
      task,
      placeTitle,
    } satisfies LocationPillDragData,
  })

  const style: React.CSSProperties = {
    transform: CSS.Translate.toString(transform),
    opacity: isDragging ? 0.4 : 1,
    touchAction: 'none',
  }

  return (
    <button
      ref={setNodeRef}
      style={style}
      type="button"
      {...listeners}
      {...attributes}
      onClick={(e) => {
        // dnd-kit suppresses click during drag; this only fires on true taps.
        e.preventDefault()
        onOpen?.(task.id)
      }}
      className="inline-flex items-center gap-1 rounded-full bg-brand-subtle border border-brand-subtle-border px-2 py-0.5 text-xs text-brand-primary hover:bg-brand-subtle/80 cursor-grab active:cursor-grabbing max-w-[180px]"
      title={placeTitle ? `${task.title} @ ${placeTitle}` : task.title}
      aria-label={
        placeTitle
          ? `Drag ${task.title} — location ${placeTitle}`
          : `Drag ${task.title} — location task`
      }
      aria-roledescription="draggable location-tied task"
    >
      <MapPin className="h-3 w-3 flex-shrink-0" />
      <span className="truncate">{task.title}</span>
    </button>
  )
}

export default LocationPill
